import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { 
  User, 
  AuthState, 
  LoginCredentials, 
  RegisterCredentials, 
  AuthResponse 
} from '@/types';

class SupabaseAuthService {
  private client: SupabaseClient;
  private authStateCallbacks: ((state: AuthState) => void)[] = [];

  constructor(supabaseUrl: string, supabaseKey: string) {
    this.client = createClient(supabaseUrl, supabaseKey);
    this.setupAuthListener();
  }

  private setupAuthListener(): void {
    this.client.auth.onAuthStateChange((_event, session) => {
      const authState: AuthState = {
        user: session?.user ? this.mapSupabaseUserToUser(session.user) : null,
        isAuthenticated: !!session?.user,
        isLoading: false,
        error: null
      };
      
      this.notifyAuthStateChange(authState);
    });
  }

  private mapSupabaseUserToUser(supabaseUser: any): User {
    return {
      id: supabaseUser.id,
      email: supabaseUser.email || '',
      username: supabaseUser.user_metadata?.username || supabaseUser.email?.split('@')[0] || '',
      avatar: supabaseUser.user_metadata?.avatar_url,
      isOnline: true,
      lastSeen: new Date()
    };
  }

  private notifyAuthStateChange(state: AuthState): void {
    this.authStateCallbacks.forEach(callback => callback(state));
  }

  /**
   * Subscribe to authentication state changes
   * @param callback Function to call when auth state changes
   * @returns Unsubscribe function
   */
  onAuthStateChange(callback: (state: AuthState) => void): () => void {
    this.authStateCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.authStateCallbacks.indexOf(callback);
      if (index > -1) {
        this.authStateCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Sign in with email and password
   */
  async signIn(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await this.client.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) throw error;
      if (!data.user || !data.session) throw new Error('Authentication failed');

      return {
        user: this.mapSupabaseUserToUser(data.user),
        session: {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
          expires_at: data.session.expires_at || 0
        }
      };
    } catch (error) {
      throw new Error(`Sign in failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Sign up with email, password, and username
   */
  async signUp(credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await this.client.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: {
            username: credentials.username,
          },
        },
      });

      if (error) throw error;
      if (!data.user || !data.session) throw new Error('Registration failed');

      return {
        user: this.mapSupabaseUserToUser(data.user),
        session: {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
          expires_at: data.session.expires_at || 0
        }
      };
    } catch (error) {
      throw new Error(`Sign up failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      const { error } = await this.client.auth.signOut();
      if (error) throw error;
    } catch (error) {
      throw new Error(`Sign out failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get current user session
   */
  async getSession(): Promise<AuthResponse | null> {
    try {
      const { data: { session }, error } = await this.client.auth.getSession();
      
      if (error) throw error;
      if (!session || !session.user) return null;

      return {
        user: this.mapSupabaseUserToUser(session.user),
        session: {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at || 0
        }
      };
    } catch (error) {
      console.error('Failed to get session:', error);
      return null;
    }
  }

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const { data: { user }, error } = await this.client.auth.getUser();
      
      if (error) throw error;
      return user ? this.mapSupabaseUserToUser(user) : null;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  /**
   * Refresh current session
   */
  async refreshSession(): Promise<AuthResponse | null> {
    try {
      const { data, error } = await this.client.auth.refreshSession();
      
      if (error) throw error;
      if (!data.session || !data.user) return null;

      return {
        user: this.mapSupabaseUserToUser(data.user),
        session: {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
          expires_at: data.session.expires_at || 0
        }
      };
    } catch (error) {
      console.error('Failed to refresh session:', error);
      return null;
    }
  }

  /**
   * Get access token for API requests
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const { data: { session }, error } = await this.client.auth.getSession();
      if (error) throw error;
      return session?.access_token || null;
    } catch (error) {
      console.error('Failed to get access token:', error);
      return null;
    }
  }
}

export default SupabaseAuthService;